import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import { Building2, TrendingUp, Wallet, Percent, Home, Hammer, BedDouble, Bath, MessageSquare, Send, Loader2, Paperclip, FileText, FileDown, CheckCircle2, Circle, Check, X, Plus, Workflow as WorkflowIcon } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import ProjectThread from "@/components/ProjectThread";
import { WorkflowTracker } from "@/components/WorkflowTracker";

const QUOTE_BADGE = {
  Submitted: "bg-blue-100 text-blue-700",
  Accepted: "bg-emerald-100 text-emerald-700",
  Declined: "bg-slate-200 text-slate-600",
};

const STATUS_STYLE = {
  "Awaiting Contractor Approval": "bg-amber-100 text-amber-800",
  "Awaiting Funds from Landlord": "bg-red-100 text-red-700",
  "On Hold": "bg-slate-200 text-slate-700",
  "Completed": "bg-emerald-100 text-emerald-700",
  "In Progress": "bg-blue-100 text-blue-700",
};

export default function LandlordPortal() {
  const [data, setData] = useState(null);
  const [wfModal, setWfModal] = useState(null);
  const load = () => api.get("/portal/landlord").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <PortalLayout title="My Portfolio" icon={Building2}><Loading /></PortalLayout>;

  const s = data.summary;
  const acc = data.access || { schedule: true, messages: true, accounts: true };
  const cards = [
    { label: "Properties", value: s.portfolio, icon: Home, color: "text-blue-700 bg-blue-50" },
    ...(acc.accounts ? [
      { label: "Rent Collected", value: gbp(s.rent), icon: TrendingUp, color: "text-emerald-700 bg-emerald-50" },
      { label: "My Balance", value: gbp(s.balance ?? 0), icon: Wallet, color: "text-violet-700 bg-violet-50" },
      { label: "Management Fees", value: gbp(s.fees), icon: Percent, color: "text-amber-700 bg-amber-50" },
    ] : []),
  ];

  return (
    <PortalLayout title="My Portfolio" subtitle="Landlord" icon={Building2}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {cards.map((c, i) => (
          <Card key={i} className="p-5 fade-up" data-testid={`ll-stat-${i}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color} mb-4`}><c.icon size={20} /></div>
            <p className="text-2xl font-display font-bold tracking-tight text-slate-900 tabular-nums">{c.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{c.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">My Properties</h3>
          <div className="space-y-4">
            {data.properties.map((p) => (
              <Card key={p.property_id} className="overflow-hidden flex fade-up" data-testid={`ll-property-${p.reference}`}>
                <img src={p.cover_file_id ? fileUrl(p.cover_file_id) : p.image} alt="" className="w-28 object-cover" />
                <div className="p-4 flex-1">
                  <div className="flex items-center gap-2 mb-1"><Badge>{p.status}</Badge></div>
                  <p className="font-semibold text-slate-900 text-sm truncate">{p.address}</p>
                  <p className="text-xs text-slate-400">{p.city}, {p.postcode}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><BedDouble size={13} /> {p.beds}</span>
                    <span className="flex items-center gap-1"><Bath size={13} /> {p.baths}</span>
                    <span className="ml-auto font-display font-bold text-slate-900">{gbp(p.rent_pcm)}/mo</span>
                  </div>
                  {p.letting_stage && (
                    <div className="mt-2.5 pt-2.5 border-t border-slate-100" data-testid={`ll-letting-status-${p.reference}`}>
                      <span className="text-[11px] text-slate-400">Lettings status</span>
                      <span className="ml-2 inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{p.letting_stage}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {data.projects.length > 0 && (
            <>
              <h3 className="font-display text-lg font-semibold text-slate-900 mt-8 mb-4">Refurbishment Progress</h3>
              <div className="space-y-3">
                {data.projects.map((p) => (
                  <Card key={p.project_id} className="p-4" data-testid={`ll-project-${p.project_id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Hammer size={15} className="text-amber-600" />
                      <p className="text-sm font-medium text-slate-800">{p.name}</p>
                    </div>
                    <span data-testid={`ll-project-status-${p.project_id}`} className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[p.landlord_status] || STATUS_STYLE["In Progress"]}`}>
                      {p.landlord_status || "In Progress"}
                    </span>
                    {p.refurb_stages && p.refurb_stages.length > 0 && (
                      <div className="mt-3 border-t border-slate-100 pt-3 space-y-1.5" data-testid={`ll-refurb-stages-${p.project_id}`}>
                        {p.refurb_stages.map((s, i) => {
                          const done = s.status === "Complete";
                          return (
                            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`ll-refurb-stage-${p.project_id}-${i}`}>
                              {done ? <CheckCircle2 size={15} className="text-emerald-600 shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                              <span className={done ? "text-slate-700" : "text-slate-500"}>{s.name}</span>
                              {done && s.completed_at
                                ? <span className="ml-auto text-xs font-medium text-emerald-700">{new Date(s.completed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                : <span className="ml-auto text-xs text-slate-400">{{ "In Progress": "In progress", "Available": "Not started", "Locked": "Waiting" }[s.status] || s.status || "Pending"}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <LandlordQuotes projectId={p.project_id} onChanged={load} />
                    {p.workflow_id && (
                      <button data-testid={`ll-open-workflow-${p.project_id}`} onClick={() => setWfModal(p.workflow_id)}
                        className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2 text-sm font-semibold transition-colors">
                        <WorkflowIcon size={15} /> View workflow &amp; stages
                      </button>
                    )}
                    <ProjectThread projectId={p.project_id} compact senderRole="landlord" />
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="space-y-6">
          {(data.work_orders || []).length > 0 && (
            <div data-testid="ll-work-orders">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Maintenance & Job Requests</h3>
              <div className="space-y-3">
                {data.work_orders.map((w) => <LLWorkOrder key={w.work_order_id} w={w} onChanged={load} />)}
              </div>
            </div>
          )}
          {acc.messages && <LandlordComms messages={data.messages || []} departments={data.departments || ["Admin"]} onSent={load} />}

          {acc.accounts && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="font-display text-lg font-semibold text-slate-900">Statement</h3>
              <StatementDownload properties={data.properties || []} />
            </div>
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold tracking-wider">
                  <tr><th className="py-3 px-4 text-left">Date</th><th className="text-left">Detail</th><th className="text-right px-4">Amount</th></tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr key={t.txn_id} className="border-b border-slate-100 last:border-0" data-testid={`ll-txn-${t.txn_id}`}>
                      <td className="py-3 px-4 text-xs text-slate-500 font-mono">{fmtDate(t.date)}</td>
                      <td className="text-sm text-slate-700"><span className="text-xs text-blue-700 mr-2">{t.property_address || t.property_ref}</span>{t.description}</td>
                      <td className={`text-sm px-4 text-right tabular-nums font-medium ${t.direction === "in" ? "text-emerald-600" : "text-red-500"}`}>{gbp(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <LandlordBankAccounts />
          </div>
          )}
        </div>
      </div>

      {wfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setWfModal(null)}>
          <div data-testid="ll-workflow-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-slate-900">Refurbishment workflow</h3>
              <button data-testid="ll-close-workflow-modal" onClick={() => setWfModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <WorkflowTracker workflowId={wfModal} readOnly />
          </div>
        </div>
      )}
    </PortalLayout>
  );
}

function LandlordBankAccounts() {
  const empty = { label: "", account_name: "", sort_code: "", account_number: "", notes: "" };
  const [accounts, setAccounts] = useState(null);
  const [modal, setModal] = useState(null); // null | {account?}
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portal/landlord/bank-accounts").then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
  useEffect(() => { load(); }, []);

  const open = (account = null) => {
    setForm(account ? { label: account.label || "", account_name: account.account_name || "", sort_code: account.sort_code || "", account_number: account.account_number || "", notes: account.notes || "" } : empty);
    setModal({ account });
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal.account) await api.patch(`/portal/landlord/bank-accounts/${modal.account.account_id}`, form);
      else await api.post(`/portal/landlord/bank-accounts`, form);
      toast.success(modal.account ? "Bank account updated — your agent has been notified" : "Bank account added — your agent has been notified");
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.label || a.account_name}"?`)) return;
    try { await api.delete(`/portal/landlord/bank-accounts/${a.account_id}`); toast.success("Bank account deleted — your agent has been notified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  if (accounts === null) return null;
  return (
    <div className="mt-6" data-testid="ll-bank-accounts">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-display text-lg font-semibold text-slate-900">My Bank Accounts</h3>
        <button data-testid="ll-add-bank" onClick={() => open()} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Plus size={14} /> Add account</button>
      </div>
      <p className="text-xs text-slate-400 mb-3">These are the accounts your agent pays your rental income into. Changes are flagged to your agent for verification.</p>
      {accounts.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-400" data-testid="ll-no-bank">No bank accounts yet. Add one so your agent knows where to send your funds.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {accounts.map((a) => (
            <Card key={a.account_id} className="p-4 flex items-start gap-3" data-testid={`ll-bank-${a.account_id}`}>
              <Wallet size={18} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{a.label || a.account_name || "Account"}</p>
                <p className="text-sm text-slate-500 truncate">{a.account_name}</p>
                <p className="text-xs text-slate-400">{a.sort_code}{a.account_number ? ` · ${a.account_number}` : ""}</p>
                {a.notes && <p className="text-xs text-slate-400 mt-1">{a.notes}</p>}
              </div>
              <div className="flex flex-col gap-1.5">
                <button data-testid={`ll-edit-bank-${a.account_id}`} onClick={() => open(a)} className="text-slate-400 hover:text-blue-700"><FileText size={15} /></button>
                <button data-testid={`ll-del-bank-${a.account_id}`} onClick={() => del(a)} className="text-slate-400 hover:text-red-600"><X size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="ll-bank-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">{modal.account ? "Edit" : "Add"} Bank Account</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <LLField label="Label / nickname" testid="ll-bank-label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="e.g. Main account" />
              <LLField label="Account holder name" testid="ll-bank-account-name" value={form.account_name} onChange={(v) => setForm({ ...form, account_name: v })} required />
              <div className="grid grid-cols-2 gap-3">
                <LLField label="Sort code" testid="ll-bank-sort" value={form.sort_code} onChange={(v) => setForm({ ...form, sort_code: v })} placeholder="12-34-56" />
                <LLField label="Account number" testid="ll-bank-number" value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} placeholder="12345678" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                <textarea data-testid="ll-bank-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <button type="submit" data-testid="ll-save-bank" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} {modal.account ? "Save changes" : "Add account"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const LLField = ({ label, testid, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
    <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
  </div>
);


function LLWorkOrder({ w, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/work-orders/${w.work_order_id}/quote/${action}`);
      toast.success(`Quote ${action === "accept" ? "accepted" : "declined"}`);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  const QB = { Submitted: "bg-blue-100 text-blue-700", Accepted: "bg-emerald-100 text-emerald-700", Declined: "bg-slate-200 text-slate-500" };
  return (
    <Card className="p-4" data-testid={`ll-wo-${w.work_order_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-mono text-xs text-blue-700">{w.reference}</span>
        {(w.property_address || w.property_ref) && <Badge>{w.property_address || w.property_ref}</Badge>}
      </div>
      <p className="font-semibold text-slate-900 text-sm">{w.title}</p>
      {w.description && <p className="text-xs text-slate-500 mt-0.5">{w.description}</p>}
      {w.contractor_name && <p className="text-xs text-slate-400 mt-1">Contractor: {w.contractor_name}</p>}
      {w.quote_status ? (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold tabular-nums text-slate-800">{gbp(w.quote_amount)}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${QB[w.quote_status] || ""}`}>{w.quote_status}</span>
          </div>
          {w.quote_notes && <p className="text-xs text-slate-400 mt-0.5">{w.quote_notes}</p>}
          {w.quote_status === "Submitted" && (
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => act("accept")} disabled={busy} data-testid={`ll-wo-accept-${w.work_order_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1 disabled:opacity-60"><Check size={13} /> Accept</button>
              <button onClick={() => act("decline")} disabled={busy} data-testid={`ll-wo-decline-${w.work_order_id}`} className="border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60">Decline</button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-amber-600 mt-2">Awaiting contractor quote…</p>
      )}
    </Card>
  );
}

function LandlordQuotes({ projectId, onChanged }) {
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/projects/${projectId}/quotes`).then(({ data }) => setQuotes(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const act = async (q, action) => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/quotes/${q.quote_id}/${action}`);
      toast.success(`${action === "accept" ? "Accepted" : "Declined"} ${q.contractor_name}'s quote`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update quote"); }
    finally { setBusy(false); }
  };

  if (quotes.length === 0) return null;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`ll-project-quotes-${projectId}`}>
      <p className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2"><FileText size={13} /> Contractor quotes</p>
      <div className="space-y-2">
        {quotes.map((q) => (
          <div key={q.quote_id} data-testid={`ll-quote-${q.quote_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800 tabular-nums">{gbp(q.amount)}</span>
                <span className="text-xs text-slate-500 truncate">{q.contractor_name}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${QUOTE_BADGE[q.status] || "bg-slate-100 text-slate-600"}`}>{q.status}</span>
              </div>
              {q.notes && <p className="text-xs text-slate-400 truncate">{q.notes}</p>}
            </div>
            {q.status === "Submitted" && (
              <div className="shrink-0 flex items-center gap-1.5">
                <button onClick={() => act(q, "accept")} disabled={busy} data-testid={`ll-accept-quote-${q.quote_id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
                  <Check size={13} /> Accept
                </button>
                <button onClick={() => act(q, "decline")} disabled={busy} data-testid={`ll-decline-quote-${q.quote_id}`}
                  className="border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
                  Decline
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatementDownload({ properties = [] }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(() => properties.map((p) => p.property_id));
  const multi = properties.length > 1;
  const toggle = (pid) => setSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const allSelected = sel.length === properties.length;
  const download = async () => {
    if (multi && sel.length === 0) { toast.error("Select at least one property"); return; }
    setBusy(true);
    try {
      const params = [];
      if (from) params.push(`from=${from}`);
      if (to) params.push(`to=${to}`);
      if (multi && !allSelected) params.push(`property_ids=${sel.join(",")}`);
      const res = await api.get(`/portal/landlord/statement${params.length ? "?" + params.join("&") : ""}`, { responseType: "blob" });
      let fname = "Statement.pdf";
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) fname = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate statement"); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full space-y-2" data-testid="ll-statement-download">
      {multi && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="ll-stmt-property-select">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Include properties</span>
            <button type="button" onClick={() => setSel(allSelected ? [] : properties.map((p) => p.property_id))}
              className="text-[11px] text-blue-700 hover:underline">{allSelected ? "Clear all" : "Select all"}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {properties.map((p) => (
              <label key={p.property_id} data-testid={`ll-stmt-prop-${p.reference}`}
                className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs cursor-pointer transition-colors ${sel.includes(p.property_id) ? "border-blue-300 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600"}`}>
                <input type="checkbox" checked={sel.includes(p.property_id)} onChange={() => toggle(p.property_id)} className="accent-blue-600" />
                <span>{p.address || p.reference}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="block text-[11px] text-slate-400 mb-0.5">From</label>
          <input data-testid="ll-stmt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-0.5">To</label>
          <input data-testid="ll-stmt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <button data-testid="ll-stmt-download-btn" onClick={download} disabled={busy}
          className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download PDF
        </button>
      </div>
    </div>
  );
}

function LandlordComms({ messages, departments, onSent }) {
  const [dept, setDept] = useState(departments[0] || "Admin");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const thread = messages.filter((m) => (m.department || "Admin") === dept);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length, dept]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { department: dept, body: text.trim() });
      setText("");
      await onSent();
    } finally { setSending(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "message");
        fd.append("entity_id", dept);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        const { data: rec } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/messages", { department: dept, body: "", file_id: rec.file_id, file_name: rec.original_filename, file_category: rec.category });
      }
      await onSent();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="landlord-comms">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <p className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><MessageSquare size={16} className="text-emerald-600" /> Messages</p>
        <p className="text-xs text-slate-400">Choose which team to message — they'll know who needs to reply.</p>
      </div>
      <div className="px-4 pt-3 flex flex-wrap gap-2">
        {departments.map((d) => (
          <button key={d} data-testid={`ll-dept-${d}`} onClick={() => setDept(d)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${dept === d ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {d}
          </button>
        ))}
      </div>
      <div className="p-4 space-y-2 max-h-72 overflow-y-auto" data-testid="ll-thread">
        {thread.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No messages with the {dept} team yet. Say hello 👋</p>}
        {thread.map((m) => {
          const mine = m.from_role === "landlord";
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                {!mine && <p className="text-[11px] font-semibold text-slate-500 mb-0.5">{m.from_name}</p>}
                {m.file_id && (
                  m.file_category === "photo" ? (
                    <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer"><img src={fileUrl(m.file_id)} alt={m.file_name} className="rounded-lg max-h-40 mb-1" /></a>
                  ) : m.file_category === "video" ? (
                    <video src={fileUrl(m.file_id)} controls className="rounded-lg max-h-40 mb-1" />
                  ) : (
                    <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 mb-1 underline ${mine ? "text-white" : "text-blue-700"}`}>
                      <FileText size={14} /> {m.file_name || "Attachment"}
                    </a>
                  )
                )}
                {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                <p className={`text-[10px] mt-1 ${mine ? "text-emerald-100" : "text-slate-400"}`}>{fmtDate(m.date)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-slate-100">
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="ll-msg-file"
          accept="image/*,video/*,application/pdf,.xls,.xlsx,.csv,.doc,.docx" onChange={onUpload} />
        <button type="button" data-testid="ll-msg-attach" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-10 h-10 shrink-0 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-60" title="Attach PDF, photo, video or Excel">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
        </button>
        <input data-testid="ll-msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={`Message the ${dept} team…`}
          className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <button type="submit" data-testid="ll-msg-send" disabled={sending || !text.trim()}
          className="w-10 h-10 shrink-0 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center disabled:opacity-50">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </Card>
  );
}
